import { type CamelCase, type DeepCamelKeys, deepCamelKeys, deepSnakeKeys } from 'string-ts'

// Built-in object types have to pass through untouched: mapping over their keys would
// strip their identity, leaving a bag of method signatures rather than a Blob or Date.
// The generated `Body_*` upload types carry `Blob | File`, so this is load-bearing.
type BuiltInObject = Blob | Date | File | ((...args: never[]) => unknown)

// Some generated types are keyed by number literals (e.g. the SDG 1-17 breakdowns, which
// 0.86 emitted as '1' but 0.99 emits as 1). DeepCamelKeys only maps string keys, and drops
// the rest — `{ 1: Contribution; total_pct: number }` camelises to just `{ totalPct: number }`.
// Normalising keys to their string form first keeps those properties. At runtime object keys
// are strings anyway, so this only corrects the type.
type StringifyKeys<T> = T extends BuiltInObject
  ? T
  : T extends readonly unknown[]
    ? { [Index in keyof T]: StringifyKeys<T[Index]> }
    : T extends object
      ? { [Key in keyof T as `${Key & (string | number)}`]: StringifyKeys<T[Key]> }
      : T

type Camelize<T> = DeepCamelKeys<StringifyKeys<T>>

export type { CamelCase, Camelize }

// Currently our types are generated so that:
//   - Type is defined in python code like `foo: str | None`
//   - Gets generated in OpenAPI spec as `foo: string` (as opposed to foo*: string,
//     indicating it is required).
//   - The TypeScript type gets generated as `foo?: string`
// However, the value is really `foo: string | null`. Therefore, by recursively checking
// for null values and converting them to undefined, we can be more confident the
// types are correct. Ideally we'd find a way to generate the OpenAPI spec to skip
// straight to generating `foo: string | null`, rendering this step unnecessary.
const replaceNullWithUndefined = <T extends object | unknown>(obj: T): T | unknown => {
  if (obj === null) {
    return undefined
  }

  if (typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.map((item) => replaceNullWithUndefined(item))
    } else {
      const result: Record<string, unknown> = {}
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          result[key] = replaceNullWithUndefined(obj[key])
        }
      }
      return result
    }
  }

  return obj
}

export const snakeToCamelKeys = <T>(inputObject: T) => {
  const inputUndefinedToNull = replaceNullWithUndefined(inputObject)
  return deepCamelKeys(inputUndefinedToNull as T) as Camelize<T>
}

export const camelToSnakeKeys = <T>(inputObject: T) => deepSnakeKeys(inputObject)
